import React, { Component } from 'react';

// Components
import EmptyStateCard from '@/views/components/misc/EmptyStateCard';
import LegacyContactPortfolios from '@/views/containers/contacts/contact-details/tabs/LegacyContactPortfolios';
import DataSourceTabs from '@/views/components/data-source/DataSourceTabs';
import PortfolioUploadModal from '@/views/components/modals/portfolio-upload/PortfolioUploadModal';
import useThemeVersion from '@/hooks/useThemeVersion';
import { DeletePortfoliosModal, EditPortfolioModal } from '@/views/components/modals';
import { LoadingSpinner } from '@/views/components/common';
import { PortfoliosTableV2 } from '@/views/components/tables';
import { ContactPortfoliosHeader } from './components/ContactPortfoliosHeader';

// Enums
import { HoldingAllocationMode } from '@/enums/Portfolio';
import { PERSISTENCE_KEYS, resetLocalStoragePersistenceSource } from '@/utils/persistence';
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '@/enums/Portfolio';

// Libraries
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { ColumnSort } from '@tanstack/react-table';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { withRouter, RouteComponentProps } from 'react-router-dom';

// Redux
import { addPortfoliosToComparison, ADD_PORTFOLIOS_TO_COMPARISON } from '@/redux/modules/comparison';
import {
  acceptContactPortfolio,
  clearNewlyCreatedPortfolioIds,
  DELETE_CONTACT_PORTFOLIOS,
  deleteContactPortfolios,
  getContactPortfolios,
  saveContactPortfolio,
  setPersistenceSource,
} from '@/redux/modules/contacts';
import { clonePortfolio, getPortfolioById } from '@/redux/modules/portfolios';
import { loadPortfolioForEdit, resetPortfolio, setPortfolioContact, setPortfolioEditorContext } from '@/redux/modules/edit-portfolio';
import { logNewPortfolio } from '@/services/usage-log';
import { ReduxState } from '@/redux/modules/types';
import { selectIsLoadingEvery, selectIsLoadingParamsEvery, selectIsLoadingSome, startLoaderAction, stopLoaderAction } from '@/redux/modules/ui/loaders';
import { togglePortfolioEditor } from '@/redux/modules/common/actionCreators';
import { setIsActivePortfolioPostMessage } from '@/redux/modules/post-message';
import { getCustomProxies } from '@/redux/modules/custom-proxies';
import { shouldClosePortfolioEditor } from '@/redux/modules/ai-statement-reader-multi-portfolio';

// Types
import Dictionary from '@/types/dictionary';
import GenericContact from '@/types/generic-contact';
import Portfolio from '@/types/portfolio';
import User from '@/types/user';
import { AIStatementReaderMultiPortfolioState } from '@/redux/modules/types/ai-statement-reader-multi-portfolio';
import { CustomProxiesData } from '@/types/custom-proxies-state';
import { DataSourceTabs as DataSourceTabsType } from '@/types/app-config';
import { SortingField } from '@/types/data';

// Utils
import disclaimers from '@/utils/disclaimers';
import heapUtils, { HEAP_CUSTOM_EVENTS } from '@/utils/3rd-party/heap';
import localStorageUtils from '@/utils/local-storage';
import notification from '@/utils/notification';
import {
  clonePortfolioAsProxy,
  getPortfolioMissingOrInvalidHoldings,
  loadPortfolioIfHoldingsAreMissing,
  loadPortfoliosIfHoldingsAreMissing,
} from '@/utils/portfolio/utils';
import { withLoaderAction } from '@/utils/promise';
import { formatMessage } from '@/utils/i18n';
import { getFilteredDataSourceTabs } from '@/utils/data-source';
import { getEmptyPortfolio, PORTFOLIO_TYPES, PortfolioEditorContext } from '@/utils/portfolios';
import { NAV_PATHS, PortfolioFlag } from '@/utils/constants';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '@/utils/permissions';
import { showError } from '@/utils/errors';
import { getTableSortedColumns } from '@/utils/data-table';
import { DATA_SOURCE_TAB_CATEGORY } from '@/enums/DataSource';
import { canAccessUploadCSV } from '@/utils/feature-access';

type MatchParams = {
  contactId: string;
};

type StateProps = {
  comparingPortfolioIds: string[];
  contact?: GenericContact;
  editPortfolioModalIsVisible: boolean;
  evaluatingPortfolioForComparison: boolean;
  hasMorePortfolios: boolean;
  isDeletingContactPortfolio: boolean;
  loading: boolean;
  persistenceSource?: string;
  portfolios?: Portfolio[];
  user?: User | null;
  isPostMessageActive: boolean;
  aiStatementReaderMultiPortfolio: AIStatementReaderMultiPortfolioState;
  newlyCreatedPortfolioIds?: string[];
  shouldCloseEditor: boolean;
};

type DispatchProps = {
  addPortfoliosToComparison: (portfolios: Portfolio[]) => Promise<void>;
  clearNewlyCreatedPortfolioIds: () => void;
  deleteContactPortfolios: (portfolios: Portfolio[], contactId: string) => Promise<{ portfolioIds: string[] }>;
  getContactPortfolios: (query?: string, sort?: SortingField[], shouldFetchNext?: boolean) => Promise<Portfolio[]>;
  loadPortfolioForEdit: (portfolio: Portfolio) => Promise<void>;
  resetPortfolio: () => void;
  saveContactPortfolio: (portfolio: Portfolio) => Promise<Portfolio>;
  setPortfolioContact: (contact: GenericContact) => void;
  logNewPortfolio: () => Promise<void>;
  togglePortfolioEditor: (show: boolean) => void;
  getPortfolioById: (
    id: string,
    portfolioType?: string,
    contact?: string,
    enterprise?: any,
    errorWhiteList?: string[],
    source?: Portfolio,
  ) => Promise<Portfolio>;
  clonePortfolio: (portfolio: Portfolio) => void;
  acceptContactPortfolio: (contactId: string, portfolioId: string) => Promise<void>;
  setIsActivePortfolioPostMessage: (isActive: boolean) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;
  startLoaderAction: (name: string) => void;
  stopLoaderAction: (name: string) => void;
  setPortfolioEditorContext: (editorContext: PortfolioEditorContext) => void;
  getCustomProxies: () => Promise<CustomProxiesData>;
};

type Props = RouteComponentProps<MatchParams> &
  StateProps &
  DispatchProps & {
    nextContactPortfoliosToken?: string;
  };

type State = {
  activeDataSource: string;
  hasPendingComparison: boolean;
  query: string;
  searching: boolean;
  selectedPortfoliosForDelete: Portfolio[];
  showDeletePortfolioModal: boolean;
  showPortfolioUploadModal: boolean;
  sortingFields: SortingField[];
  sortedColumns: ColumnSort[];
};
export class ContactPortfoliosV2 extends Component<Props, State> {
  private dataSourceTabs: DataSourceTabsType[] | null = getFilteredDataSourceTabs(
    this.props.user,
    DATA_SOURCE_TAB_CATEGORY.PORTFOLIOS,
    this.props.isPostMessageActive,
  );

  private i18nEmptyStatePath = 'EmptyStateCard.ContactDetailsPortfolios';

  state = {
    activeDataSource: this.dataSourceTabs?.[0]?.value ?? '',
    hasPendingComparison: false,
    query: '',
    searching: false,
    selectedPortfoliosForDelete: [],
    showDeletePortfolioModal: false,
    showPortfolioUploadModal: false,
    sortingFields: [],
    sortedColumns: [],
  };

  componentDidMount() {
    const { isPostMessageActive, user, setIsActivePortfolioPostMessage, getCustomProxies } = this.props;

    if (user) {
      // Reset the Persistence Source when the component mounts
      const isPostMessageDataSourceTabExist = this.dataSourceTabs?.find(tab => tab.isPostMessage);
      const isInitialDataSourceTabPostMessage = isPostMessageActive && isPostMessageDataSourceTabExist;

      if (isInitialDataSourceTabPostMessage) {
        setIsActivePortfolioPostMessage(false);
      }
      resetLocalStoragePersistenceSource(localStorageUtils.STORAGE_KEYS.CONTACT_PORTFOLIO_PERSISTENCE_SOURCE, user, () => this.searchPortfolios());

      // load proxies so that these are available when editing portfolios or adding to comparison
      if (userHasPermission(user, PERMISSIONS.CUSTOM_PROXIES)) {
        getCustomProxies && getCustomProxies();
      }
    }
  }

  componentWillUnmount() {
    const { clearNewlyCreatedPortfolioIds } = this.props;

    // Clear Redux state on unmount to ensure it's gone on navigation/refresh
    clearNewlyCreatedPortfolioIds();
    this.props.getContactPortfolios('', []);
  }

  searchPortfolios = debounce(() => {
    const { query, sortingFields } = this.state;
    this.props.getContactPortfolios(query, sortingFields).then(() => {
      this.setState({ searching: false });
    });
  }, 250);

  onSearch = (query: string) => {
    this.setState({ query, searching: true });
    this.searchPortfolios();
  };

  onLoadMore = () => {
    this.searchPortfolios();
  };

  onDeletePortfolio = (portfolios: Portfolio[]) => {
    this.setState({
      selectedPortfoliosForDelete: portfolios,
      showDeletePortfolioModal: true,
    });
  };

  onCancelDeletePortfolio = () => {
    this.setState({ showDeletePortfolioModal: false });
  };

  onConfirmDeletePortfolios = () => {
    const { selectedPortfoliosForDelete: portfolios } = this.state;
    const { contactId } = this.props.match.params;
    if (!portfolios) {
      return;
    }

    this.props
      .deleteContactPortfolios(portfolios, contactId)
      .then(res => {
        if (portfolios.length !== res.portfolioIds.length) {
          notification.error(formatMessage('Portfolios.DeleteFailure'));
        } else {
          notification.success(formatMessage('Portfolios.DeleteSuccess'));
        }
        this.onCancelDeletePortfolio();
      })
      .catch(error => {
        notification.error(formatMessage('Portfolios.DeleteFailure'));
      });
  };

  onEditPortfolio = (inPortfolio: Portfolio, pendingComparison?: boolean) => {
    const { user, getPortfolioById } = this.props;

    this.props.setPortfolioEditorContext(PortfolioEditorContext.PORTFOLIO);

    loadPortfolioIfHoldingsAreMissing(inPortfolio, getPortfolioById)
      .then(portfolio => {
        const toLoad = disclaimers.portfolioHasMissingOrInvalidHoldings(portfolio) ? clonePortfolioAsProxy(portfolio) : portfolio;

        if (user && userHasPermissionEnabled(user, PERMISSIONS.PORTFOLIO_UNITS_MODE)) {
          toLoad.allocationMode = HoldingAllocationMode.Units;
        }

        this.props
          .loadPortfolioForEdit(toLoad)
          .then(() => {
            this.setState({ hasPendingComparison: !!pendingComparison });
          })
          .catch(showError);
      })
      .catch(showError);
  };

  onAcceptPortfolio = (portfolio: Portfolio) => {
    const { acceptContactPortfolio, contact } = this.props;
    const { query } = this.state;
    if (contact?._id && portfolio._id) {
      acceptContactPortfolio(contact?._id, portfolio._id).then(() => {
        this.onSearch(query);
      });
    }
  };

  onCancelEditPortfolio = () => {
    this.props.resetPortfolio();
    this.props.togglePortfolioEditor(false);
  };

  onEditPortfolioSave = async (portfolio: Portfolio): Promise<Portfolio> => {
    const { contact, shouldCloseEditor } = this.props;
    const { hasPendingComparison, query, sortingFields } = this.state;

    if (contact && !portfolio.contact) {
      portfolio.contact = contact;
      this.props.setPortfolioContact(contact);
      this.props.logNewPortfolio();
    }

    const newPortfolio = await this.props.saveContactPortfolio(portfolio);

    if (shouldCloseEditor) {
      this.props.resetPortfolio();
    }

    if (hasPendingComparison) {
      this.onComparePortfolios([newPortfolio]);
    } else if (shouldCloseEditor) {
      this.props.togglePortfolioEditor(false);
    }

    this.props.getContactPortfolios(query, sortingFields);
    return newPortfolio;
  };

  onComparePortfolios = async (portfolioList: Portfolio[]) => {
    const { getPortfolioById, startLoaderAction, stopLoaderAction } = this.props;

    await withLoaderAction(ADD_PORTFOLIOS_TO_COMPARISON.type, startLoaderAction, stopLoaderAction, async () => {
      const portfolios = await loadPortfoliosIfHoldingsAreMissing(portfolioList, getPortfolioById);

      const portfolioWithMissingHolding = portfolios.find(portfolio => disclaimers.portfolioHasMissingOrInvalidHoldings(portfolio));
      if (portfolioWithMissingHolding) {
        this.onEditPortfolio(portfolioWithMissingHolding, true);
        return;
      }

      await this.props.addPortfoliosToComparison(portfolios);
      heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);
      this.setState({ hasPendingComparison: false });
      this.props.togglePortfolioEditor(false);
      this.props.history.push(NAV_PATHS.COMPARISON, { from: this.props.location });
    });
  };

  onCreatePortfolio = () => {
    const { contact, clearNewlyCreatedPortfolioIds } = this.props;
    if (!contact) {
      return;
    }

    // Clear highlighted IDs when creating a new portfolio
    clearNewlyCreatedPortfolioIds();

    this.props.resetPortfolio();
    this.props.setPortfolioEditorContext(PortfolioEditorContext.PORTFOLIO);
    this.props.loadPortfolioForEdit(getEmptyPortfolio());
    this.props.setPortfolioContact(contact);
    this.props.logNewPortfolio();
  };

  getDisplayedColumns = () => {
    const { user } = this.props;
    const { activeDataSource } = this.state;
    const isPostMessageTab = this.dataSourceTabs?.find(tab => tab.value === activeDataSource)?.isPostMessage || false;
    const isPersistenceSourceTab = this.dataSourceTabs?.find(tab => tab.value === activeDataSource)?.persistenceKey || false;

    return [
      {
        key: PORTFOLIO_TABLE_COLUMNS.NAME,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.ID,
        hidden: !userHasPermission(user, PERMISSIONS.PORTFOLIOS_SHOW_PORTFOLIO_ID) || (!isPostMessageTab && !isPersistenceSourceTab),
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.PLAN_ACCOUNT,
        hidden: !userHasPermissionEnabled(user, PERMISSIONS.PLANNING),
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.RISK_PROFILE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LAST_MODIFIED,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LOCK,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS,
        hidden: !userHasPermissionEnabled(user, PERMISSIONS.SHOW_PORTFOLIO_DETAILS),
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.COMPARE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.MENU_TOGGLE,
      },
    ]
      .filter(col => !col.hidden)
      .map(col => col.key);
  };

  showPortfolioUploadModal = () => this.setState({ showPortfolioUploadModal: true });

  closePortfolioUploadModal = () => this.setState({ showPortfolioUploadModal: false });

  onCanAccept = (portfolio: Portfolio): boolean => {
    const { user } = this.props;

    return (
      user != null &&
      userHasPermission(user, PERMISSIONS.CONTACTS_PORTFOLIOS_ACCEPT) &&
      localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PORTFOLIO_PERSISTENCE_SOURCE) == null &&
      !portfolio.flags?.includes(PortfolioFlag.AcceptedPortfolio)
    );
  };

  onCanEdit = (portfolio: Portfolio): boolean => {
    const { user } = this.props;

    return userHasPermission(user, PERMISSIONS.PORTFOLIOS_EDIT) && (!portfolio.availableActions || portfolio.availableActions.edit);
  };

  onCanClone = (portfolio: Portfolio): boolean => {
    const { user } = this.props;

    return userHasPermission(user, PERMISSIONS.PORTFOLIOS_CLONE) && (!portfolio.availableActions || portfolio.availableActions.clone);
  };

  onCanDelete = (portfolio: Portfolio): boolean => {
    const { user } = this.props;

    return userHasPermission(user, PERMISSIONS.PORTFOLIOS_DELETE) && (!portfolio.availableActions || portfolio.availableActions.delete);
  };

  getEmptyStateCard = (): JSX.Element => {
    const { contact, user } = this.props;

    const canCreate = userHasPermission(user, PERMISSIONS.PORTFOLIOS_CREATE);
    const canUpload = canAccessUploadCSV(user);

    const primaryButton = canCreate
      ? {
          label: `${this.i18nEmptyStatePath}.PrimaryButtonText`,
          onClick: this.onCreatePortfolio,
        }
      : undefined;

    const secondaryActions = {
      links: canCreate && canUpload ? [{ label: `${this.i18nEmptyStatePath}.SecondaryLinkText1`, onClick: this.showPortfolioUploadModal }] : undefined,
    };

    return (
      <EmptyStateCard
        i18nVariables={{ contactName: contact?.fname || '' }}
        messagePath={`${this.i18nEmptyStatePath}.Message`}
        primaryButton={primaryButton}
        titlePath={`${this.i18nEmptyStatePath}.Title`}
        {...secondaryActions}
      />
    );
  };

  hasPersistenceKeySource = () => {
    const { user } = this.props;
    return user != null && localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PORTFOLIO_PERSISTENCE_SOURCE) != null;
  };

  onSelectDataSource = (dataSourceValue: string) => {
    const { user, setIsActivePortfolioPostMessage, setPersistenceSource } = this.props;

    if (!user || !this.dataSourceTabs) return;

    this.setState({ activeDataSource: dataSourceValue });

    const activeDataSource = this.dataSourceTabs.find(tab => tab.value === dataSourceValue);
    let persistenceSource;

    if (activeDataSource?.isPostMessage) {
      setIsActivePortfolioPostMessage(true);
    } else {
      setIsActivePortfolioPostMessage(false);
      persistenceSource = activeDataSource?.persistenceKey;
    }

    setPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PORTFOLIO_PERSISTENCE_SOURCE, persistenceSource);
    this.searchPortfolios();
  };

  renderDataSourceTabs = () => {
    const { user } = this.props;

    const isContactPersistenceSourceActive =
      user && localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE) != null;
    const contactDataSourceTabs: DataSourceTabsType[] = get(this.props.user, 'appConfig.dataSourceTabs.contacts', []);
    const hasExternalContacts =
      !contactDataSourceTabs.filter(tab => tab.persistenceKey === PERSISTENCE_KEYS.EXISTING_CLIENT_CONTACT).length || isContactPersistenceSourceActive;

    return (
      !!this.dataSourceTabs?.length &&
      hasExternalContacts && (
        <div className="contact-details-data-source-tabs">
          <DataSourceTabs disabled={this.props.loading} tabs={this.dataSourceTabs} value={this.state.activeDataSource} onChange={this.onSelectDataSource} />
        </div>
      )
    );
  };

  handleSorting = (sort: ColumnSort) => {
    const { query } = this.state;
    const { serverSortingParams, columnSort } = getTableSortedColumns(this.state.sortingFields, sort);
    this.setState({ sortingFields: serverSortingParams, sortedColumns: columnSort });
    this.props.getContactPortfolios(query, serverSortingParams, false);
  };

  onClonePortfolio = (portfolio: Portfolio) => {
    const { contactId } = this.props.match.params;
    this.props.getPortfolioById(portfolio._id!, PORTFOLIO_TYPES.CLIENT, contactId, undefined, undefined, portfolio).then((p: Portfolio) => {
      this.props.clonePortfolio(p);
    });
  };

  renderPortfolioTable = (isTableHidden: boolean, displayedColumns: PORTFOLIO_TABLE_COLUMNS[], displayedMetrics: PORTFOLIO_METRICS_FIELDS[]) => {
    const { comparingPortfolioIds, hasMorePortfolios, loading, newlyCreatedPortfolioIds, portfolios } = this.props;
    const { query, sortedColumns } = this.state;

    return (
      <>
        {loading ? (
          <LoadingSpinner className="initial-loading" message={formatMessage('Contacts.ContactTabs.Portfolios.LoadingPortfolios')} />
        ) : (
          <PortfoliosTableV2<Portfolio>
            className={classNames({ 'visually-hidden': isTableHidden })}
            comparingPortfolioIds={comparingPortfolioIds}
            displayedColumns={displayedColumns}
            displayedMetrics={displayedMetrics}
            highlightedIds={newlyCreatedPortfolioIds}
            onAccept={this.onAcceptPortfolio}
            onCompare={this.onComparePortfolios}
            onDelete={this.onDeletePortfolio}
            onEdit={this.onEditPortfolio}
            onCopy={this.onClonePortfolio}
            onLoadMore={this.onLoadMore}
            portfolios={portfolios}
            query={query}
            selectedTitle="PortfoliosTable.SelectedTitle.Portfolios"
            showLoadMore={hasMorePortfolios}
            missingProductPredicate={portfolio => getPortfolioMissingOrInvalidHoldings(portfolio).length > 0}
            canEditPredicate={portfolio => this.onCanEdit(portfolio)}
            canDeletePredicate={portfolio => this.onCanDelete(portfolio)}
            canClonePredicate={portfolio => this.onCanClone(portfolio)}
            canAcceptPredicate={portfolio => this.onCanAccept(portfolio)}
            serverSideSorting
            onServerSideSorting={sort => this.handleSorting(sort)}
            sortedColumns={sortedColumns}
          />
        )}
      </>
    );
  };

  render() {
    const { contact, editPortfolioModalIsVisible, evaluatingPortfolioForComparison, isDeletingContactPortfolio, loading, portfolios, user } = this.props;
    const { activeDataSource, query, searching, selectedPortfoliosForDelete, showDeletePortfolioModal, showPortfolioUploadModal } = this.state;

    const isTableHidden = !query && (loading || !portfolios?.length);
    const hasPersistenceKeySource = this.hasPersistenceKeySource();
    const isActiveDataSourceTabPostMessage = this.dataSourceTabs?.find(tab => tab.value === activeDataSource)?.isPostMessage || false;

    const displayedColumns = this.getDisplayedColumns();

    const displayedMetrics = [
      PORTFOLIO_METRICS_FIELDS.MER,
      PORTFOLIO_METRICS_FIELDS.YEAR_ONE,
      PORTFOLIO_METRICS_FIELDS.YEAR_THREE,
      PORTFOLIO_METRICS_FIELDS.SIZE,
      PORTFOLIO_METRICS_FIELDS.HOLDINGS,
    ];

    const showEmptyCard = !loading && !searching && !query && !portfolios?.length;

    return (
      <div className="contact-portfolios-v2 table-wrapper table-container">
        <ContactPortfoliosHeader
          contact={contact}
          evaluatingPortfolioForComparison={evaluatingPortfolioForComparison}
          hasPersistenceKeySource={hasPersistenceKeySource}
          isActiveDataSourceTabPostMessage={isActiveDataSourceTabPostMessage}
          onSearch={this.onSearch}
          onCreatePortfolio={this.onCreatePortfolio}
          portfolios={portfolios}
          query={query}
          searching={searching}
          showPortfolioUploadModal={this.showPortfolioUploadModal}
          user={user}
        />
        {this.renderDataSourceTabs()}
        <div className="contact-portfolios-body">
          {showEmptyCard ? this.getEmptyStateCard() : this.renderPortfolioTable(isTableHidden, displayedColumns, displayedMetrics)}
        </div>
        <DeletePortfoliosModal
          isDeleting={isDeletingContactPortfolio}
          onCancel={this.onCancelDeletePortfolio}
          onConfirm={this.onConfirmDeletePortfolios}
          portfolios={selectedPortfoliosForDelete}
          show={showDeletePortfolioModal}
        />

        <EditPortfolioModal show={editPortfolioModalIsVisible} onCancel={this.onCancelEditPortfolio} onSave={this.onEditPortfolioSave} />

        <PortfolioUploadModal show={showPortfolioUploadModal} titleId="Modal.PortfolioUpload.UploadClientPortfolio" onCancel={this.closePortfolioUploadModal} />
      </div>
    );
  }
}

const ContactPortfolios = (props: Props) => {
  const { isLightVersion } = useThemeVersion();

  if (isLightVersion) {
    return <ContactPortfoliosV2 {...props} />;
  }

  return <LegacyContactPortfolios {...props} />;
};

const mapStateToProps = (state: ReduxState): StateProps => {
  const loadingParams = selectIsLoadingParamsEvery(state, [ADD_PORTFOLIOS_TO_COMPARISON.type]);
  const comparingPortfolioIds = loadingParams.reduce((acc, param: Dictionary<unknown>) => {
    if (param.portfolios) {
      return [...acc, ...(param.portfolios as Portfolio[]).map(portfolio => portfolio._id!)];
    }

    return acc;
  }, [] as string[]);
  return {
    hasMorePortfolios: !!state.contacts.current?.hasMore.portfolios,
    contact: state.contacts.current?.contact,
    persistenceSource: state.contacts.persistenceSource,
    user: state.auth.user,
    portfolios: state.contacts.current?.portfolios,
    loading: state.contacts.loading.current.portfolios,
    comparingPortfolioIds,
    isDeletingContactPortfolio: selectIsLoadingEvery(state, [DELETE_CONTACT_PORTFOLIOS.type]),
    editPortfolioModalIsVisible: state.comparison.portfolioEditor.isVisible,
    evaluatingPortfolioForComparison: selectIsLoadingSome(state, [ADD_PORTFOLIOS_TO_COMPARISON.type]),
    isPostMessageActive: state.postMessage.isPostMessageActive,
    aiStatementReaderMultiPortfolio: state.aiStatementReaderMultiPortfolio,
    newlyCreatedPortfolioIds: state.contacts.current?.newlyCreatedPortfolioIds,
    shouldCloseEditor: shouldClosePortfolioEditor(state.aiStatementReaderMultiPortfolio),
  };
};

export default withRouter(
  connect(mapStateToProps, {
    resetPortfolio,
    addPortfoliosToComparison,
    clearNewlyCreatedPortfolioIds,
    deleteContactPortfolios,
    getContactPortfolios,
    loadPortfolioForEdit,
    saveContactPortfolio,
    setPortfolioContact,
    logNewPortfolio,
    togglePortfolioEditor,
    getPortfolioById,
    acceptContactPortfolio,
    setIsActivePortfolioPostMessage,
    setPersistenceSource,
    startLoaderAction,
    stopLoaderAction,
    setPortfolioEditorContext,
    clonePortfolio,
    getCustomProxies,
  })(ContactPortfolios),
);
